<template>
	<div>
		<StandardModal
			open
			dark
			v-if="desktopScreenData"
			:title="$t('meetings_share_screen_source_choose')"
			:confirmText="$t('chat_messageaction_share')"
			:confirmProps="{ disabled: !selectedSource }"
			@confirm="onScreenSourceSelect(selectedSource)"
			@cancel="onScreenSourceSelect()"
		>
			<Tabs
				:tabs="tabs"
				alignLeft
				stretch
				dark
				selectedBottom
				@change="onChangeScreenTab"
			>
				<template v-slot="{ tab }">
					{{ $t(`meetings_share_screen_source_tab_${tab}`) }}
				</template>
			</Tabs>
			<div class="screen-source-list" v-if="selectedTab">
				<div
					v-for="screen in desktopScreenData[selectedTab]"
					:key="screen.id"
					class="screen-source"
					:class="{ selected: selectedSource === screen }"
					@click="selectedSource = screen"
				>
					<img :src="screen.thumbnail" />
					<div class="screen-source-info" v-if="screen.isWindow">
						<img
							class="screen-source-icon"
							v-if="screen.appIcon"
							:src="screen.appIcon"
						/>
						<div class="screen-source-name" v-text="screen.name" />
					</div>
				</div>
			</div>
		</StandardModal>
	</div>
</template>

<script>
import OT from '@opentok/client'
import { StandardModal, Tabs } from '@/components/common'
import {
	setPublisher,
	setScreenPublisher,
	setPublishing,
	setSharing,
	removeScreenPublisher,
	getMyConnectionId,
	getSession,
	getPublisher,
	getUserPrefs,
	listenForActiveSpeaker,
	startStream,
	getScreenPublisher,
	getVideoSession,
	watchForBrowserMicChange,
	logMeetingEvent,
} from './data'
import { showAlert, hideAlert } from './MeetingAlert'
import { MicOffIcon } from '@/icons'
import {
	getInitialDevice,
	syncPublisherCaptions,
	videoResolutions,
	createPublisherCaptionsSubscriber,
} from './data/common'

const FHD = `${videoResolutions.FHD.width}x${videoResolutions.FHD.height}`
const HD = `${videoResolutions.HD.width}x${videoResolutions.HD.height}`
const MAX_SCREEN_SIZE = 1920
const MUTED_WARNING_TIME = 8000
const MUTED_WARNING_THRESHOLD = 30 // scale of 0 to 100

const options = {
	width: '100%',
	height: '100%',
	fitMode: 'contain',
	showControls: false,
	style: {
		nameDisplayMode: 'off',
		buttonDisplayMode: 'off',
		videoDisabledDisplayMode: 'off',
		audioBlockedDisplayMode: 'off',
		audioLevelDisplayMode: 'off',
		archiveStatusDisplayMode: 'off',
	},
}

const desktopSourceOptions = {
	fetchWindowIcons: true,
	thumbnailSize: {
		height: 300,
		width: 300,
	},
}

export default {
	name: 'Publisher',
	components: { StandardModal, Tabs },
	props: {
		videoSource: {
			type: String,
			default: 'video',
		},
	},

	data() {
		const tabs = ['screen', 'window']
		return {
			tabs,
			mutedAlert: null,
			desktopScreenData: null,
			selectedSource: null,
			selectedTab: tabs[0],
		}
	},

	emits: ['stream'],

	computed: {
		isVideo() {
			return this.videoSource === 'video'
		},
		isScreen() {
			return this.videoSource === 'screen'
		},
	},

	async mounted() {
		this.$onEventBus({
			videosessionUpdated: () => syncPublisherCaptions(),
			browserMicrophoneVolume: this.onVolumeChange,
			userPrefsUpdated: ({ video, audio }, old) => {
				const publisher = getPublisher()
				if (!publisher) return
				if (video !== old.video) {
					publisher.publishVideo(video)
				}
				if (audio !== old.audio) {
					publisher.publishAudio(audio)
				}
			},
		})

		const config = await this.getPublisherConfig()
		if (!config) {
			logMeetingEvent(
				'publisher',
				'cannot publish because getPublisherConfig failed',
				config
			)
			return
		}
		logMeetingEvent('Initializing publisher with config...', config)
		const publisher = OT.initPublisher(this.$el, config, (err) => {
			if (err) {
				EventBus.$emit('tokboxError', err.name)
				logMeetingEvent('publisher', 'initialization err', err)
				if (this.isVideo) {
					setPublishing(false)
				} else {
					setSharing(false)
				}
				return
			}
			publisher.initialConfig = config
			if (this.isVideo) {
				setPublisher(publisher)
				watchForBrowserMicChange()
				syncPublisherCaptions()
			} else {
				setScreenPublisher(publisher)
			}

			const session = getSession()
			if (session) {
				if (session.isConnected()) {
					this.publish(publisher)
				}
				session.on('sessionConnected', this.publish)
			}
		})

		publisher.on('accessAllowed', () => {
			EventBus.$emit('publisherAccessAllowed', true)
			logMeetingEvent('publisher', 'accessAllowed')
		})

		publisher.on('accessDenied', (event) => {
			logMeetingEvent('publisher', 'accessDenied', event)
		})

		publisher.on('mediaStopped', (event) => {
			logMeetingEvent('publisher', 'mediaStopped', event)
			if (!event.track) {
				// no media track, this usually occurs when a screen share is stopped from the browser
				const screenPub = getScreenPublisher()
				if (screenPub && screenPub.streamId === event.target.streamId) {
					EventBus.$emit('screenShareStopped')
					return
				}
			}

			if (event.track.kind === 'audio') {
				EventBus.$emit('lostAudioDevice', event.target.streamId)
			} else if (event.track.kind === 'video') {
				EventBus.$emit('lostVideoDevice', event.target.streamId)
			}
		})

		publisher.on('destroyed', (event) => {
			logMeetingEvent('publisher', 'destroyed', event)
			EventBus.$emit('streamDestroyed', {
				id: this.isScreen ? 'screen' : 'camera',
			})
		})
	},

	methods: {
		publish(publisher) {
			logMeetingEvent('publisher', 'publishing publisher to session...')
			const session = getSession()
			session.publish(publisher, (err) => {
				if (err) {
					logMeetingEvent('publisher', 'error publishing to session', err)
					EventBus.$emit('tokboxError', err.name)
					const connectionId = getMyConnectionId()
					if (this.isVideo) {
						setPublishing(false)
						removePublisher(connectionId)
					} else {
						setSharing(false)
						removeScreenPublisher(connectionId)
					}
				} else {
					logMeetingEvent('publisher', 'successfully publishing to session')
					if (!this.isScreen) {
						const { video } = getUserPrefs()
						if (!video) {
							publisher.publishVideo(false)
						}
					}
					if (this.isVideo) {
						setPublisher(publisher)
						watchForBrowserMicChange()
						syncPublisherCaptions()
						createPublisherCaptionsSubscriber(publisher)
					} else {
						setScreenPublisher(publisher)
					}
					startStream(publisher.stream.id, this.videoSource)
					listenForActiveSpeaker(publisher)
					this.$emit('stream', publisher.stream)
					if (getVideoSession().startmuted) {
						EventBus.$emit('publisherMuted')
					}
				}
			})

			session.on('streamPropertyChanged', ({ stream }) => {
				const publisher = getPublisher() || {}
				if (!publisher.stream || publisher.stream.id !== stream.id) return
				if (stream.hasAudio && this.mutedAlert) {
					// if our stream was muted, hide the alert if it's showing
					// and reset it so it can be reshown next time
					hideAlert(this.mutedAlert)
					setTimeout(() => {
						// delay so it alert doesn't show too frequently
						this.mutedAlert = null
					}, 3000)
				}
			})
		},

		onVolumeChange(volume) {
			const { stream } = getPublisher() || {}
			const isPublishingAndMuted = stream && !stream.hasAudio
			const haveNotYetShownAlert = !this.mutedAlert
			if (
				isPublishingAndMuted &&
				haveNotYetShownAlert &&
				volume >= MUTED_WARNING_THRESHOLD
			) {
				// show the alert for the given time and then prevent
				// the warning from being shown again until the user
				// unmutes themself
				this.mutedAlert = showAlert({
					text: this.$t('meetings_unmute_hint'),
					icon: MicOffIcon,
					time: MUTED_WARNING_TIME,
				})
			}
		},

		async getPublisherConfig() {
			const { audio, hd } = getUserPrefs()
			const videosession = getVideoSession()
			let videoSource = undefined
			try {
				videoSource = await this.getPublisherVideoSource()
			} catch (e) {
				console.error(e)
				if (this.isVideo) {
					setPublishing(false)
					this.$alert.error(this.$t('meetings_live_error_bubble'))
				} else {
					setSharing(false)
					EventBus.$emit('tokboxError', 'OT_USER_MEDIA_ACCESS_DENIED')
				}
				return null
			}
			let publishAudio = audio
			const name = { videoType: 'camera' }
			if (videoSource instanceof MediaStreamTrack) {
				// this is a screenshare from desktop
				publishAudio = false
				name.videoType = 'screen'
			} else if (videoSource === 'screen') {
				name.videoType = 'screen'
			} else if (videosession.startmuted) {
				publishAudio = false
			}
			return {
				...options,
				name: JSON.stringify(name),
				videoSource,
				// OT bug: always publish video at start, disable later if not wanted
				publishVideo: true,
				publishAudio,
				publishCaptions: videosession.captions_enabled || false,
				...(this.isScreen
					? {
							maxResolution: {
								width: MAX_SCREEN_SIZE,
								height: MAX_SCREEN_SIZE,
							},
							videoContentHint: 'detail',
							scalableScreenshare: true,
					  }
					: {
							resolution: hd ? FHD : HD,
							audioSource: await getInitialDevice('audio'),
					  }),
			}
		},

		async getPublisherVideoSource() {
			if (this.isVideo) {
				const initialDevice = await getInitialDevice('video')
				return initialDevice
			}
			if (!window.todesktop) return 'screen'
			const { desktopCapturer, systemPreferences } = window.todesktop
			const access = await systemPreferences.getMediaAccessStatus('screen')
			if (access !== 'granted') {
				throw new Error(`Invalid system screen sharing permission: ${access}`)
			}
			const [windows, screens] = await Promise.all([
				desktopCapturer.getSources({
					types: ['window'],
					...desktopSourceOptions,
				}),
				desktopCapturer.getSources({
					types: ['screen'],
					...desktopSourceOptions,
				}),
			])
			windows.forEach((w) => (w.isWindow = true))
			screens.forEach((w) => (w.isScreen = true))
			return new Promise((resolve, reject) => {
				this.desktopScreenData = {
					window: windows,
					screen: screens,
					resolve,
					reject,
				}
			})
		},

		async onScreenSourceSelect(source) {
			const { resolve, reject } = this.desktopScreenData
			this.desktopScreenData = null
			if (!source) return reject('No selected video screen source')
			try {
				const stream = await navigator.mediaDevices.getUserMedia({
					audio: false,
					video: {
						mandatory: {
							chromeMediaSource: 'desktop',
							chromeMediaSourceId: source.id,
							maxWidth: MAX_SCREEN_SIZE,
							maxHeight: MAX_SCREEN_SIZE,
						},
					},
				})
				const track = stream.getVideoTracks()[0]
				resolve(track)
			} catch (e) {
				reject(e)
			}
		},

		onChangeScreenTab(tab) {
			this.selectedTab = tab
		},
	},
}
</script>
<style lang="scss" scoped>
@import 'src/sass/variables';

:deep(.modal-body) {
	@include flex-column;

	.tabs {
		height: 60px;
	}
}

.screen-source-list {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	padding: 16px;
	max-height: calc(100% - 60px);
	overflow: auto;

	.screen-source {
		@include flex-column;
		width: calc(50% - 4px);
		padding: 10px;
		border-radius: 6px;
		border: 1px solid transparent;
		height: 200px;
		gap: 10px;
		cursor: pointer;
		&:hover,
		&.selected {
			background: $neutral-900;
		}
		&.selected {
			border-color: var(--teal-300);
		}
		> img {
			flex-grow: 1;
			width: 100%;
			height: calc(100% - 35px);
			object-fit: contain;
		}
		.screen-source-info {
			@include flex;
			gap: 8px;
			.screen-source-icon {
				width: 24px;
				height: 24px;
				object-fit: contain;
				margin-top: -2px;
			}
			.screen-source-name {
				@include ellipsis;
				flex-grow: 1;
				font-size: 14px;
			}
		}
	}
}
</style>
